import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const BORDER_COLORS = {
  safety_flag: '#d32f2f',
  critical_alert_sla: '#d32f2f',
  critical_client: '#ed6c02',
  urgent_alert: '#ed6c02',
  high_alert: '#ffa726',
  high_client: '#ffa726',
  triage_review: '#1976d2',
  safety_substance: '#1976d2'
};

const ACTION_TYPE_LABELS = {
  safety_flag: 'Safety Flag',
  critical_alert_sla: 'SLA Breach',
  critical_client: 'Critical Client',
  urgent_alert: 'Urgent Alert',
  high_alert: 'High Alert',
  high_client: 'High Priority',
  triage_review: 'Pending Triage',
  safety_substance: 'Substance Safety'
};

const TIER_COLORS = {
  critical: 'error',
  high: 'warning',
  moderate: 'info',
  routine: 'primary'
};

function ActionCard({ item, onNavigateToClient }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const borderColor = BORDER_COLORS[item.actionType] || '#9e9e9e';
  const chipLabel = ACTION_TYPE_LABELS[item.actionType] || item.actionType;
  const buttonColor = TIER_COLORS[item.tier] || 'primary';

  const relativeTime = item.timestamp
    ? formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })
    : '';

  const handlePrimaryAction = (e) => {
    e.stopPropagation();
    if (item.primaryAction?.route) {
      navigate(item.primaryAction.route);
    }
  };

  const handleClientClick = (e) => {
    e.stopPropagation();
    if (onNavigateToClient) {
      onNavigateToClient(item.clientId);
    }
  };

  return (
    <Card
      onClick={() => setExpanded((prev) => !prev)}
      sx={{
        width: 240,
        minHeight: 200,
        flexShrink: 0,
        borderLeft: `4px solid ${borderColor}`,
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Chip label={chipLabel} size="small" sx={{ fontSize: '0.675rem', height: 20 }} />
          {relativeTime && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.675rem' }}>
              {relativeTime}
            </Typography>
          )}
        </Box>

        <Typography
          variant="subtitle2"
          fontWeight="bold"
          onClick={handleClientClick}
          sx={{
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
            mb: 0.5
          }}
        >
          {item.clientName}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: 0.25
          }}
        >
          {item.summary}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mb: 1
          }}
        >
          {item.detail}
        </Typography>

        <Collapse in={expanded} timeout="auto">
          <Box sx={{ mb: 1 }}>
            {item.metadata?.reasons && (
              <List dense disablePadding>
                {item.metadata.reasons.map((reason, idx) => (
                  <ListItem key={idx} disablePadding sx={{ pl: 1 }}>
                    <ListItemText
                      primary={reason}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            {item.metadata?.daysSinceReview != null && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                Last reviewed {item.metadata.daysSinceReview} days ago
              </Typography>
            )}
            <Box mt={0.5} pl={1}>
              <Button variant="outlined" size="small" sx={{ fontSize: '0.675rem' }}>
                Mark Reviewed
              </Button>
            </Box>
          </Box>
        </Collapse>

        <Box display="flex" alignItems="center" gap={1} mt="auto">
          <Button
            variant="contained"
            size="small"
            color={buttonColor}
            onClick={handlePrimaryAction}
            sx={{ fontSize: '0.7rem', textTransform: 'none' }}
          >
            {item.primaryAction?.label || 'View'}
          </Button>
          {item.secondaryAction && (
            <Button
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{ fontSize: '0.7rem', textTransform: 'none' }}
            >
              {item.secondaryAction.label}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default ActionCard;
